import React, { useState, useEffect } from "react";
import { COMPANY_INFO } from "../data/siteData";

const SERVICE_OPTIONS = [
  { id: "Vinduespudsning", label: "Vinduespudsning", icon: "fa-sparkles" },
  { id: "Solcellevask", label: "Solcellevask", icon: "fa-solar-panel" },
  { id: "Fliserens", label: "Fliserens", icon: "fa-brush" },
  { id: "Algebehandling", label: "Algebehandling", icon: "fa-spray-can-sparkles" }
];

export default function ContactForm({
  id = "tilbud",
  title = "Udfyld formularen og få et tilbud",
  subtitle = "",
  defaultService = "Vinduespudsning",
  defaultCity = "",
  defaultPostnr = "",
  variant = "default", // "default" | "hero"
  showDirectContact = false,
  className = ""
}) {
  const isHero = variant === "hero";

  const [formData, setFormData] = useState({
    fulde_navn: "",
    email: "",
    telefon: "",
    vejnavn_husnr: "",
    postnr: defaultPostnr || (defaultCity ? defaultCity : ""),
    service_type: defaultService || "Vinduespudsning",
    website_url: "" // Honeypot
  });

  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (defaultService) {
      // Map any legacy service names to one of the 4 standard services
      let matched = defaultService;
      if (defaultService.toLowerCase().includes("fliserens")) matched = "Fliserens";
      else if (defaultService.toLowerCase().includes("alge")) matched = "Algebehandling";
      else if (defaultService.toLowerCase().includes("solcelle")) matched = "Solcellevask";
      else matched = "Vinduespudsning";
      setFormData((prev) => ({ ...prev, service_type: matched }));
    }
  }, [defaultService]);

  useEffect(() => {
    if (defaultPostnr || defaultCity) {
      setFormData((prev) => ({
        ...prev,
        postnr: prev.postnr || defaultPostnr || defaultCity || ""
      }));
    }
  }, [defaultPostnr, defaultCity]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleServiceSelect = (service) => {
    setFormData((prev) => ({ ...prev, service_type: service }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.fulde_navn.trim()) newErrors.fulde_navn = "Angiv venligst dit fulde navn";
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email)) {
      newErrors.email = "Angiv venligst en gyldig emailadresse";
    }
    if (!formData.telefon.trim() || formData.telefon.trim().length < 8) {
      newErrors.telefon = "Angiv venligst et gyldigt telefonnummer (mindst 8 cifre)";
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Spam honeypot check
    if (formData.website_url) {
      setStatus("success");
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setStatus("submitting");
      setErrorMessage("");

      const workMakerPayload = {
        name: formData.fulde_navn.trim(),
        fulde_navn: formData.fulde_navn.trim(),
        email: formData.email.trim(),
        phone: formData.telefon.trim(),
        telefon: formData.telefon.trim(),
        address: formData.vejnavn_husnr.trim(),
        vejnavn_husnr: formData.vejnavn_husnr.trim(),
        postal_code: formData.postnr.trim(),
        postnr: formData.postnr.trim(),
        service: formData.service_type || "Vinduespudsning",
        service_type: formData.service_type || "Vinduespudsning",
        message: [
          `Ønsket ydelse: ${formData.service_type || "Vinduespudsning"}`,
          formData.vejnavn_husnr.trim() ? `Adresse: ${formData.vejnavn_husnr.trim()}` : null,
          formData.postnr.trim() ? `Postnr/By: ${formData.postnr.trim()}` : null
        ].filter(Boolean).join(", ")
      };

      // 1. Send direkte til WorkMaker webhook
      // WorkMaker understøtter form-data, URL-encoded og query-parametre. Da app2.workmaker.dk
      // ikke sender CORS-preflight headers (Access-Control-Allow-Origin) til cross-origin browser-kald,
      // benyttes en standard URLSearchParams POST-anmodning med mode: 'no-cors' eller query-parametre,
      // så browseren kan levere dataene fejlfrit til webhooket.
      const wmParams = new URLSearchParams();
      wmParams.append("name", formData.fulde_navn.trim());
      wmParams.append("fulde_navn", formData.fulde_navn.trim());
      wmParams.append("email", formData.email.trim());
      wmParams.append("phone", formData.telefon.trim());
      wmParams.append("telefon", formData.telefon.trim());
      wmParams.append("address", formData.vejnavn_husnr.trim());
      wmParams.append("vejnavn_husnr", formData.vejnavn_husnr.trim());
      wmParams.append("postal_code", formData.postnr.trim());
      wmParams.append("postnr", formData.postnr.trim());
      wmParams.append("service", formData.service_type || "Vinduespudsning");
      wmParams.append("service_type", formData.service_type || "Vinduespudsning");
      wmParams.append(
        "message",
        [
          `Ønsket ydelse: ${formData.service_type || "Vinduespudsning"}`,
          formData.vejnavn_husnr.trim() ? `Adresse: ${formData.vejnavn_husnr.trim()}` : null,
          formData.postnr.trim() ? `Postnr/By: ${formData.postnr.trim()}` : null
        ].filter(Boolean).join(", ")
      );

      let workmakerSent = false;
      try {
        await fetch("https://app2.workmaker.dk/api/integrations/SubmitLeadForm/86be1678-8d52-49b2-b42c-f28bed89109a", {
          method: "POST",
          mode: "no-cors",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded"
          },
          body: wmParams.toString()
        });
        workmakerSent = true;
      } catch (wmErr) {
        console.warn("WorkMaker webhook request:", wmErr);
      }

      // 2. Bevar eksisterende afsendelse til clientflow-lead
      let existingSuccess = false;
      try {
        const response = await fetch("https://api.dev.auxo.dk/fn/clientflow-lead", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(formData)
        });
        if (response.ok) {
          existingSuccess = true;
        }
      } catch (existingErr) {
        console.warn("Eksisterende integration kald fejlede:", existingErr.message);
      }

      // Hvis enten WorkMaker eller eksisterende integration lykkedes
      if (workmakerSent || existingSuccess) {
        setStatus("success");
        setFormData({
          fulde_navn: "",
          email: "",
          telefon: "",
          vejnavn_husnr: "",
          postnr: defaultPostnr || (defaultCity ? defaultCity : ""),
          service_type: defaultService || "Vinduespudsning",
          website_url: ""
        });
      } else {
        throw new Error("Der opstod en fejl under indsendelsen. Prøv venligst igen eller ring til os.");
      }
    } catch (err) {
      setStatus("error");
      setErrorMessage("Kunne ikke sende formularen. Ring i stedet til 30 14 60 70.");
    }
  };

  return (
    <div
      id={id}
      className={`bg-white rounded-2xl shadow-2xl border border-slate-200/90 text-slate-900 scroll-mt-28 sm:scroll-mt-32 ${
        isHero ? "p-5 sm:p-7 lg:p-7" : "p-6 sm:p-8 lg:p-10"
      } ${className}`}
    >
      <div className={isHero ? "mb-5" : "mb-6"}>
        <h3
          className={`font-black text-slate-900 tracking-tight ${
            isHero ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"
          }`}
        >
          {title}
        </h3>
        {subtitle && (
          <p className="mt-1 text-slate-600 text-xs sm:text-sm">
            {subtitle}
          </p>
        )}
      </div>

      {status === "success" ? (
        <div className="p-6 sm:p-8 bg-emerald-50 border border-emerald-200 rounded-xl text-center space-y-4 animate-in fade-in duration-300">
          <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto text-xl">
            <i className="fa-solid fa-circle-check" aria-hidden="true" />
          </div>
          <h4 className="text-lg sm:text-xl font-bold text-emerald-900">
            Mange tak for din henvendelse!
          </h4>
          <p className="text-emerald-800 text-xs sm:text-sm max-w-md mx-auto">
            Vi har modtaget dine oplysninger og vender hurtigt tilbage med et uforpligtende tilbud.
          </p>
          <button
            type="button"
            onClick={() => setStatus("idle")}
            className="mt-3 px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-semibold transition-colors cursor-pointer"
          >
            Send en ny forespørgsel
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3.5" noValidate>
          {/* Honeypot field (hidden from real users) */}
          <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
            <label htmlFor={`website_url_${id}`}>Website URL (leave blank)</label>
            <input
              type="text"
              id={`website_url_${id}`}
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {status === "error" && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-start gap-2.5">
              <i className="fa-solid fa-circle-exclamation text-red-500 mt-0.5 shrink-0" aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}

          {/* Fulde navn */}
          <div>
            <label htmlFor={`fulde_navn_${id}`} className="block text-xs font-bold text-slate-700 mb-1">
              Fulde navn <span className="text-cyan-600">*</span>
            </label>
            <input
              type="text"
              id={`fulde_navn_${id}`}
              name="fulde_navn"
              required
              placeholder="F.eks. Anders Jensen"
              value={formData.fulde_navn}
              onChange={handleChange}
              className={`w-full px-3.5 py-2.5 rounded-lg border bg-white text-slate-900 text-sm transition-all focus:outline-none focus:ring-2 ${
                errors.fulde_navn
                  ? "border-red-400 focus:ring-red-400"
                  : "border-slate-300 focus:ring-cyan-500 focus:border-cyan-500"
              }`}
            />
            {errors.fulde_navn && (
              <p className="mt-1 text-xs text-red-600">{errors.fulde_navn}</p>
            )}
          </div>

          {/* Email & Telefon */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Email */}
            <div>
              <label htmlFor={`email_${id}`} className="block text-xs font-bold text-slate-700 mb-1">
                Email <span className="text-cyan-600">*</span>
              </label>
              <input
                type="email"
                id={`email_${id}`}
                name="email"
                required
                placeholder="f.eks. anders@mail.dk"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-3.5 py-2.5 rounded-lg border bg-white text-slate-900 text-sm transition-all focus:outline-none focus:ring-2 ${
                  errors.email
                    ? "border-red-400 focus:ring-red-400"
                    : "border-slate-300 focus:ring-cyan-500 focus:border-cyan-500"
                }`}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Telefon */}
            <div>
              <label htmlFor={`telefon_${id}`} className="block text-xs font-bold text-slate-700 mb-1">
                Telefon <span className="text-cyan-600">*</span>
              </label>
              <input
                type="tel"
                id={`telefon_${id}`}
                name="telefon"
                required
                placeholder="F.eks. 30 14 60 70"
                value={formData.telefon}
                onChange={handleChange}
                className={`w-full px-3.5 py-2.5 rounded-lg border bg-white text-slate-900 text-sm transition-all focus:outline-none focus:ring-2 ${
                  errors.telefon
                    ? "border-red-400 focus:ring-red-400"
                    : "border-slate-300 focus:ring-cyan-500 focus:border-cyan-500"
                }`}
              />
              {errors.telefon && (
                <p className="mt-1 text-xs text-red-600">{errors.telefon}</p>
              )}
            </div>
          </div>

          {/* Vejnavn og husnr. & Postnr. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Vejnavn og husnr. */}
            <div>
              <label htmlFor={`vejnavn_husnr_${id}`} className="block text-xs font-bold text-slate-700 mb-1">
                Vejnavn og husnr.
              </label>
              <input
                type="text"
                id={`vejnavn_husnr_${id}`}
                name="vejnavn_husnr"
                placeholder="F.eks. Bøstrupvej 23"
                value={formData.vejnavn_husnr}
                onChange={handleChange}
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Postnr. */}
            <div>
              <label htmlFor={`postnr_${id}`} className="block text-xs font-bold text-slate-700 mb-1">
                Postnr.
              </label>
              <input
                type="text"
                id={`postnr_${id}`}
                name="postnr"
                placeholder="F.eks. 4200"
                value={formData.postnr}
                onChange={handleChange}
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>

          {/* Hvilken type service? (Dropdown) */}
          <div className="pt-1">
            <label htmlFor={`service_type_${id}`} className="block text-xs font-bold text-slate-700 mb-1">
              Hvilken type service?
            </label>
            <div className="relative">
              <select
                id={`service_type_${id}`}
                name="service_type"
                value={formData.service_type}
                onChange={handleChange}
                style={{ backgroundColor: "#ffffff", color: "#0f172a" }}
                className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 !bg-white text-slate-900 text-sm transition-all focus:!bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 appearance-none cursor-pointer pr-10 shadow-xs"
              >
                <option value="Vinduespudsning" style={{ backgroundColor: "#ffffff", color: "#0f172a" }} className="bg-white text-slate-900">Vinduespudsning</option>
                <option value="Solcellevask" style={{ backgroundColor: "#ffffff", color: "#0f172a" }} className="bg-white text-slate-900">Solcellevask</option>
                <option value="Fliserens" style={{ backgroundColor: "#ffffff", color: "#0f172a" }} className="bg-white text-slate-900">Fliserens</option>
                <option value="Algebehandling" style={{ backgroundColor: "#ffffff", color: "#0f172a" }} className="bg-white text-slate-900">Algebehandling</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-500">
                <i className="fa-solid fa-chevron-down text-xs" aria-hidden="true" />
              </div>
            </div>
          </div>

          {/* Submit CTA */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={status === "submitting"}
              className="w-full py-3.5 px-6 rounded-full bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm sm:text-base transition-all shadow-md hover:shadow-lg shadow-cyan-600/25 flex items-center justify-center gap-2.5 cursor-pointer disabled:opacity-70 transform active:scale-95"
            >
              {status === "submitting" ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                  <span>Sender forespørgsel...</span>
                </>
              ) : (
                <>
                  <span>Få et tilbud</span>
                  <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true" />
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {showDirectContact && (
        <div className="mt-4 pt-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <i className="fa-solid fa-phone text-cyan-600" aria-hidden="true" />
            <span>Spørgsmål? Ring:</span>
            <a href={COMPANY_INFO.phoneTel} className="font-bold text-slate-900 hover:text-cyan-600 whitespace-nowrap">
              {COMPANY_INFO.phone}
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
